import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_URLS, BACKEND_URL } from "../config";
import { apiRequest } from "../utils/apiRequest";
import AccessDenied from "../Components/AccessDenied";
import { BsThreeDots } from "react-icons/bs";
import { FiPlus, FiEdit, FiEye, FiRefreshCw } from "react-icons/fi";
import "./../css/Styles.css";
import "./master/Vendors.css";


// Per-company card colours, keyed by company name (case-insensitive).
// Companies not listed here fall back to the default teal theme in Styles.css.
const COMPANY_THEMES = {
    "APS GROUP": {
        banner: "linear-gradient(90deg, #ff3c3c, #b5112a)",
        logo: "linear-gradient(135deg,#ff3c3c, #b5112a)",
        button: "#b5112a",
        title: "#b5112a",
    },
    "INTASOHN": {
        banner: "linear-gradient(90deg, #c2ffa7, #095773)",
        logo: "linear-gradient(135deg, #a8e89a 0%, #3a9a7a 100%)",
        button: "#095773",
        title: "#095773",
    },
    "INTTAS": {
        banner: "linear-gradient(90deg, #1163c7 , #2e80e4)",
        logo: "linear-gradient(135deg, #1163c7 , #2e80e4)",
        button: "#1163c7",
        title: "#1163c7",
    },
};

const companyThemeStyle = (name) => {
    const t = COMPANY_THEMES[(name || "").trim().toUpperCase()];
    if (!t) return undefined;
    return {
        "--card-banner": t.banner,
        "--card-logo": t.logo,
        "--card-button": t.button,
        "--card-title": t.title,
    };
};

const CompanyManagement = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(null);
    const [toast, setToast] = useState(null);
    const [cardMenu, setCardMenu] = useState(null);
    const [brokenLogos, setBrokenLogos] = useState(() => new Set());

    const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        const r = await apiRequest(API_URLS.COMPANIES, {}, token);
        if (!r) return;
        if (r.denied) { setDenied(r); setLoading(false); return; }
        if (r.ok) setCompanies(r.data || []);
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

    // A save on the company page comes back here with a toast message in router state.
    useEffect(() => {
        if (!location.state?.toast) return;
        showToast(location.state.toast);
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const openAdd = () => navigate("/company-management/new");
    const openView = (c) => navigate(`/company-management/${c.id}`);
    const openEdit = (c) => navigate(`/company-management/${c.id}/edit`);

    const handleToggle = async (c) => {
        const r = await apiRequest(`${API_URLS.COMPANIES}/${c.id}/toggle-status`, { method: "PUT" }, token);
        if (!r) return;
        if (r.ok) {
            setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, status: r.data.status } : x));
            showToast(`Company ${r.data.status === "active" ? "activated" : "deactivated"}`);
        } else showToast(r.data?.message || "Failed.", "error");
    };

    if (loading) return <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>Loading companies…</div>;
    if (denied) return <AccessDenied message={denied.message} />;

    return (
        <div className="vendor-page" style={{ background : "#f3f3f3"}}>
            {toast && (
                <div style={{
                    position: "fixed", top: 70, right: 20, zIndex: 9999, padding: "10px 18px", fontWeight: 500, fontSize: 13,
                    background: toast.type === "error" ? "#fee2e2" : "#dcfce7",
                    color: toast.type === "error" ? "#991b1b" : "#166534",
                    border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                }}>
                    {toast.msg}
                </div>
            )}

            <div className="management-card" style={{ border : "none"}}>
                {/* Header */}
                <div className="management-header" style={{ cursor: "default" , background : "#fff", border : "none" , borderRadius : ".5rem" , marginBottom : "1rem",  padding : "1rem 1.5rem"}}>
                    <div className="header-title">
                        <h2>Company Management</h2>
                        <span className="vendor-badge">{companies.length}</span>
                    </div>
                    <button className="new-user-btn" onClick={openAdd}><FiPlus size={13} /> New Company</button>
                </div>

                <div className="vendor-table-container" style={{ borderRadius : ".75rem" , background : "#fff"}}>
                            {companies.length > 0 && (
                <div className="company-card-grid">
                    {companies.map(c => (
                        <div key={c.id} className="company-card" style={companyThemeStyle(c.companyName)}
                            onClick={() => openView(c)} title={`View ${c.companyName}`}>
                            <div className="company-banner" />
                            <div className="company-logo">
                                {c.logo && !brokenLogos.has(c.logo)
                                    ? <img src={`${BACKEND_URL}${c.logo}`} alt={c.companyName}
                                        onError={() => setBrokenLogos(prev => new Set(prev).add(c.logo))} />
                                    : <span>{c.companyName?.[0]}</span>}
                            </div>

                            <div className="company-content-wrapper">
                                <div className="company-actions" onClick={e => e.stopPropagation()}>
                                    <div className="company-menu-wrap">
                                        <button title="More" onClick={() => setCardMenu(m => m === c.id ? null : c.id)}><BsThreeDots /></button>
                                        {cardMenu === c.id && (
                                            <div className="company-menu">
                                                <button onClick={() => { setCardMenu(null); openView(c); }}><FiEye size={13} /> View</button>
                                                <button onClick={() => { setCardMenu(null); openEdit(c); }}><FiEdit size={13} /> Edit</button>
                                                <button onClick={() => { setCardMenu(null); handleToggle(c); }}><FiRefreshCw size={13} /> {c.status === "active" ? "Deactivate" : "Activate"}</button>
                                            </div>
                                        )}
                                    </div>
                                    {c.jobInitial && <button className="company-initial" title="Job Initial">{c.jobInitial}</button>}
                                    <button className="company-type">{c.companyType || "Domestic"}</button>
                                </div>

                                <div className="company-content">
                                    <h2>{c.companyName}</h2>
                                    <p className="company-admin">{c.companyAdmin?.fullName || "—"}</p>
                                    <span className={`company-status ${c.status}`}>{c.status === "active" ? "Active" : "Inactive"}</span>
                                    <p className="company-users"><b>{c.userCount ?? 0}</b> Users</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
                </div>
            </div>
        </div>
    );
};

export default CompanyManagement;